import { getPartnerCircleSponsorshipUpsertRequest } from '@rhg/utils/partner';
import {
    setStoredIdpToken,
    setStoredUserId,
    clearSAMLStorage,
    getSSOSessionStorageData,
} from '@rhg/utils/sso';
import { useEffect } from 'react';
import { usePartnerById } from '../use-partner';
import { usePartnerEligibility } from '../use-partner-eligibility';
import { getTokenData, useTokenData } from '../use-token-data';

export function useSSOFlow() {
    const {
        token,
        decodedToken,
        isSaml,
        isFetched: isTokenFetched,
        isError: isTokenError,
        error: tokenError,
    } = useTokenData();

    const {
        isFetched: isPartnerEligibilityFetched,
        isError: isPartnerEligibilityError,
        partnerError,
        eligibilityError,
        eligibility,
        isEligible,
        partnerId,
        partner,
        hasPreviouslyLinkedAccount,
    } = useSSOTokenPartnerEligibility(token);

    const isFetched =
        isTokenFetched &&
        (isPartnerEligibilityFetched || isPartnerEligibilityError);

    const isError = isTokenError || isPartnerEligibilityError;

    // Sets token storage if token is valid
    useEffect(() => {
        if (isSaml && !!token && !!decodedToken && !isTokenError) {
            setStoredUserId(decodedToken.sub!);
            setStoredIdpToken(token);
        }

        if (isTokenError) {
            clearSAMLStorage();
        }
    }, [decodedToken, isSaml, isTokenError, token]);

    return {
        isFetched,
        isError,
        tokenError,
        partnerError,
        eligibilityError,
        eligibility,
        isEligible,
        partnerId,
        partner,
        hasPreviouslyLinkedAccount,
    };
}

export function useSSOTokenPartnerEligibility(token?: string) {
    const tokenData = getTokenData(token);
    const { isSaml, tokenPartnerPortalData } = tokenData ?? {};

    const tokenPartnerId = tokenPartnerPortalData?.partner_id;
    const hasPreviouslyLinkedAccount = !!token && !isSaml;

    const {
        partner: partnerById,
        isFetched: isPartnerFetched,
        isError: isPartnerError,
        error: partnerError,
    } = usePartnerById(tokenPartnerId);

    const request = isPartnerFetched
        ? getPartnerCircleSponsorshipUpsertRequest(
              partnerById ?? undefined,
              tokenPartnerPortalData
          )
        : undefined;

    const {
        eligibility,
        isFetched: isEligibilityFetched,
        isError: isEligibilityError,
        error: eligibilityError,
    } = usePartnerEligibility(
        tokenPartnerId,
        request,
        true,
        !hasPreviouslyLinkedAccount
    );

    const partner =
        eligibility?.partnerForSponsorship ??
        eligibility?.partnerForEligibility ??
        partnerById ??
        undefined;
    const partnerId = partner?.id;

    const isError = isPartnerError || isEligibilityError;
    const isEnabled = !!token;

    const isFetched =
        isEnabled &&
        (isPartnerFetched || isPartnerError) &&
        (isEligibilityFetched || isEligibilityError);

    const isEligible = !!eligibility;

    return {
        isFetched,
        isEnabled,
        isError,
        partnerError,
        eligibilityError,
        eligibility,
        isEligible,
        partnerId,
        partner,
        hasPreviouslyLinkedAccount,
    };
}

export function useStoredSSOTokenPartnerEligibility() {
    const { ssoToken } = getSSOSessionStorageData();
    return useSSOTokenPartnerEligibility(ssoToken ?? undefined);
}
